/**
 * API Utility Functions
 * Centralized API calls for the application.
 *
 * Table mapping (Supabase):
 * - GET/POST /api/progress     → user_progress (user_id, completed_scenarios, task_completions, checkpoints, etc.)
 * - GET /api/uploads           → artifacts (all rows for user)
 * - GET /api/scenarios/:id/uploads → artifacts (filtered by scenario_id)
 * - POST /api/scenarios/:id/uploads → artifacts (insert)
 */

import axios from 'axios';
import { supabase } from '../lib/supabase';

// Same origin in production (Vercel); localhost when developing
const API_URL = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests
apiClient.interceptors.request.use(async (config) => {
  if (supabase) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
  }
  return config;
});

/**
 * Send message to a stakeholder
 */
export const sendStakeholderMessage = async (stakeholderId, message, context, scenarioId, channel) => {
  try {
    const response = await apiClient.post(
      `/api/stakeholder/${stakeholderId}/message`,
      { message, context, scenarioId, channel }
    );
    return response.data;
  } catch (error) {
    console.error('Error sending stakeholder message:', error);
    throw error;
  }
};

/**
 * Get mentor response
 */
export const getMentorResponse = async (message, mode, context) => {
  try {
    const response = await apiClient.post('/api/mentor/message', {
      message,
      mode,
      context
    });
    return response.data;
  } catch (error) {
    console.error('Error getting mentor response:', error);
    throw error;
  }
};

/**
 * Get stakeholders for a scenario and channel
 */
export const getStakeholders = async (scenarioId, channel = null) => {
  try {
    const url = channel 
      ? `/api/scenarios/${scenarioId}/stakeholders?channel=${channel}`
      : `/api/scenarios/${scenarioId}/stakeholders`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error getting stakeholders:', error);
    throw error;
  }
};

/**
 * Get conversation history
 */
export const getConversationHistory = async (scenarioId, channel) => {
  try {
    const response = await apiClient.get(
      `/api/scenarios/${scenarioId}/conversations/${channel}`
    );
    return response.data;
  } catch (error) {
    console.error('Error getting conversation history:', error);
    throw error;
  }
};

/**
 * Get conversation context from previous scenarios
 */
export const getPreviousScenarioContext = async (scenarioId, channel) => {
  try {
    const response = await apiClient.get(
      `/api/scenarios/${scenarioId}/conversations/context/${channel}`
    );
    return response.data;
  } catch (error) {
    console.error('Error getting previous scenario context:', error);
    throw error;
  }
};

/**
 * Submit scenario for scoring
 */
export const submitScenarioScore = async (scenarioId, decisions, context) => {
  try {
    const response = await apiClient.post(
      `/api/scenarios/${scenarioId}/score`,
      { decisions, context }
    );
    return response.data;
  } catch (error) {
    console.error('Error submitting scenario score:', error);
    throw error;
  }
};

/**
 * Get all saved uploads (artifacts) for the user across all scenarios - from database for workspace and pill counts
 */
export const getAllUploads = async () => {
  try {
    const response = await apiClient.get('/api/uploads');
    return response.data.uploads || [];
  } catch (error) {
    console.error('Error getting uploads:', error);
    throw error;
  }
};

/**
 * Get saved uploads (artifacts) for a scenario - for workspace display
 */
export const getScenarioUploads = async (scenarioId) => {
  try {
    const response = await apiClient.get(`/api/scenarios/${scenarioId}/uploads`);
    return response.data.uploads || [];
  } catch (error) {
    console.error('Error getting scenario uploads:', error);
    throw error;
  }
};

/**
 * Save an upload (artifact) for a scenario - persists to Supabase
 */
export const saveScenarioUpload = async (scenarioId, { fileName, taskIndex, fileType, fileData, fileSize }) => {
  if (!scenarioId) {
    console.error('[api] saveScenarioUpload: scenarioId is missing');
    throw new Error('Scenario not set; cannot save upload.');
  }
  try {
    const response = await apiClient.post(`/api/scenarios/${scenarioId}/uploads`, {
      fileName,
      taskIndex: taskIndex ?? null,
      fileType: fileType || null,
      fileData: fileData || null,
      fileSize: fileSize || null
    });
    return response.data;
  } catch (error) {
    const msg = error?.response?.data?.error || error?.message;
    const status = error?.response?.status;
    console.error('[api] saveScenarioUpload failed:', status, msg);
    throw error;
  }
};

/**
 * Get AI-generated scenario feedback and STAR artifact
 * Server fetches saved chat history from Supabase and uses uploads + scenario summary
 */
export const getScenarioFeedback = async (scenarioId, body) => {
  try {
    const response = await apiClient.post(
      `/api/scenarios/${scenarioId}/feedback`,
      body
    );
    return response.data;
  } catch (error) {
    console.error('Error getting scenario feedback:', error);
    throw error;
  }
};

/**
 * Mark onboarding as complete
 */
export const markOnboardingComplete = async () => {
  try {
    const response = await apiClient.post('/api/user/mark-onboarding-complete');
    return response.data;
  } catch (error) {
    console.error('Error marking onboarding complete:', error);
    throw error;
  }
};

/**
 * Get user profile
 */
export const getProfile = async () => {
  try {
    const response = await apiClient.get('/api/user/profile');
    return response.data;
  } catch (error) {
    console.error('Error fetching profile:', error);
    throw error;
  }
};

/**
 * Update user profile (full_name, avatar_url, linkedin_url)
 */
export const updateProfile = async (data) => {
  try {
    const response = await apiClient.put('/api/user/profile', data);
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};



